import axios from 'axios';
import { BASE_URL, CONTRACT_API_PATH, CONTRACT_UPLOAD_API_PATH } from 'constants/api';
import { ContractInterface } from 'contract/type';
import { ContractTermInterface } from 'contract/type/contractTermType';
import { ApiService } from 'service/api/apiService';
import { FetchResult } from 'type/apiType';

class ContractService {
  constructor(private apiService: ApiService<ContractInterface>) {}

  public async fetch(params: URLSearchParams): Promise<FetchResult<ContractInterface>> {
    return this.apiService.fetch(params);
  }

  // Contract search function (for reference contract selection)
  public async searchContracts(searchTerm: string): Promise<ContractInterface[]> {
    const queryParams = new URLSearchParams();
    queryParams.append('search', searchTerm);
    queryParams.append('page_size', '50');

    const result = await this.apiService.fetch(queryParams);
    return result.results;
  }

  // Fetch multiple contracts by contract IDs (for initial value loading)
  public async fetchByIds(contractIds: number[]): Promise<ContractInterface[]> {
    if (contractIds.length === 0) return [];

    const queryParams = new URLSearchParams();
    contractIds.forEach(id => queryParams.append('contract_ids', id.toString()));

    const result = await this.apiService.fetch(queryParams);
    return result.results;
  }

  // Fetch contracts by content IDs (for listing contracts linked to content)
  public async fetchByContentIds(
    contentIds: number[],
    params?: URLSearchParams
  ): Promise<FetchResult<ContractInterface>> {
    if (contentIds.length === 0)
      return {
        results: [],
        count: 0,
        next: null,
        previous: null,
        page_next: null,
        page_previous: null,
        page_size: 10,
      };

    const queryParams = params ? new URLSearchParams(params) : new URLSearchParams();
    contentIds.forEach(id => queryParams.append('content_ids', id.toString()));

    const result = await this.apiService.fetch(queryParams);
    return result;
  }

  async fetchById(id: number): Promise<ContractInterface> {
    return this.apiService.fetchById(id);
  }

  async create(contract: ContractInterface): Promise<void> {
    await this.apiService.create(contract);
  }

  async update(id: number, contract: ContractInterface): Promise<void> {
    await this.apiService.updateByPut(id, contract);
  }

  async fetchTerms(contractId: number): Promise<ContractTermInterface[]> {
    return this.apiService.fetchBySubPathRaw<ContractTermInterface[]>(`${contractId}/terms/`);
  }

  async fetchTermDetail(contractId: number, termId: number): Promise<ContractTermInterface> {
    return this.apiService.fetchBySubPathRaw<ContractTermInterface>(
      `${contractId}/terms/${termId}/`
    );
  }

  async createTerm(
    contractId: number,
    term: Partial<ContractTermInterface>
  ): Promise<ContractTermInterface> {
    return this.apiService.createBySubPath<ContractTermInterface>(`${contractId}/terms/`, term);
  }

  async updateTerm(contractId: number, termId: number, term: ContractTermInterface): Promise<void> {
    await this.apiService.updateBySubPath(`${contractId}/terms/${termId}/`, term);
  }

  async deleteTerm(contractId: number, termId: number): Promise<void> {
    await this.apiService.deleteByUrl(`${contractId}/terms/${termId}/`, {});
  }

  public async upload(file: File): Promise<void> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await axios.post(`${BASE_URL}${CONTRACT_UPLOAD_API_PATH}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }
}

export const contractService = new ContractService(new ApiService(CONTRACT_API_PATH));
